import { useState, useEffect, useCallback } from 'react'
import UserProfileService from './../../../../shared/services/UserProfile.service'

const META_KEY = 'profile---viewed-headshot-guidelines'

/**
 * Custom hook for managing Profile Pic Guidelines modal logic
 * Checks if user has viewed guidelines and manages modal state
 *
 * @returns {Object} - Guidelines state and handlers
 */
export const useGuidelinesCheck = () => {
  const [hasViewedGuidelines, setHasViewedGuidelines] = useState(null) // null = loading, true/false = loaded
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false)
  const [isFirstTimeView, setIsFirstTimeView] = useState(false) // Track if this is first-time viewing

  /**
   * Preload user meta data on component mount to avoid latency
   * Does NOT auto-open modal - only checks and stores the result
   */
  useEffect(() => {
    const checkGuidelines = async () => {
      try {
        const user = await UserProfileService.instance()
        if (!user) {
          console.error('Unable to fetch user instance')
          setHasViewedGuidelines(false)
          return
        }

        // Use User.meta(true).key() to search for the specific meta key
        // This internally uses UserMetaFactory.search() with meta_key filter
        const metaApi = user.meta(true)
        const metaResults = await metaApi.key(META_KEY)

        // Check if meta record exists
        let hasViewed = false

        if (metaResults) {
          if (Array.isArray(metaResults)) {
            if (metaResults.length > 0) {
              if (metaResults[0] && metaResults[0].curr_props) {
                const metaRecord = metaResults.find(item => item.curr_props && item.curr_props.meta_key === META_KEY)
                hasViewed = !!metaRecord
              } else {
                const metaRecord = metaResults.find(item => item && item.meta_key === META_KEY)
                hasViewed = !!metaRecord || metaResults.length > 0
              }
            }
          } else if (metaResults.models && Array.isArray(metaResults.models)) {
            hasViewed = metaResults.models.length > 0
          } else if (typeof metaResults === 'object' && metaResults !== null) {
            if (metaResults.meta_key === META_KEY) {
              hasViewed = true
            } else if (metaResults.curr_props && metaResults.curr_props.meta_key === META_KEY) {
              hasViewed = true
            }
          }
        }

        // console.log("hasViewedGuidelines RJ", hasViewed, "metaResults type:", typeof metaResults, "isArray:", Array.isArray(metaResults))
        setHasViewedGuidelines(hasViewed)
      } catch (error) {
        console.error('Error checking guidelines meta:', error)
        // Default to false on error (will show modal when they try to upload)
        setHasViewedGuidelines(false)
      }
    }

    checkGuidelines()
  }, [])

  /**
   * Check if user needs to view guidelines before uploading
   * Returns true if can proceed, false if need to show modal
   */
  const checkBeforeUpload = useCallback(() => {
    // If still loading, don't allow upload yet
    if (hasViewedGuidelines === null) {
      return false
    }

    // If user hasn't viewed guidelines, show modal
    if (!hasViewedGuidelines) {
      setIsFirstTimeView(true)
      setShowGuidelinesModal(true)
      return false
    }

    // User has viewed guidelines, can proceed
    return true
  }, [hasViewedGuidelines])

  /**
   * Handles closing of guidelines modal
   * Updates user meta to mark guidelines as viewed (only on first-time view)
   */
  const handleGuidelinesModalClose = useCallback(async () => {
    // Only update meta if this is the first-time viewing
    if (isFirstTimeView) {
      try {
        const user = await UserProfileService.instance()
        if (user) {
          const metaApi = user.meta(true)

          // Create the meta record
          const newMeta = await metaApi.create({
            meta_key: META_KEY,
            meta_value: 'true',
          })

          // Important: Must call save() to persist to database
          if (newMeta && typeof newMeta.save === 'function') {
            await newMeta.save()
          }

          setHasViewedGuidelines(true)
        }
      } catch (error) {
        console.error('Error updating guidelines meta:', error)
        // Even on error, mark as viewed to prevent blocking user
        setHasViewedGuidelines(true)
      }
      setIsFirstTimeView(false)
    }

    // Close modal
    setShowGuidelinesModal(false)
  }, [isFirstTimeView])

  /**
   * Toggles guidelines modal (for manual open via button)
   */
  const toggleGuidelinesModal = useCallback(() => {
    setShowGuidelinesModal(prev => !prev)
  }, [])

  return {
    hasViewedGuidelines,
    showGuidelinesModal,
    isFirstTimeView,
    checkBeforeUpload,
    handleGuidelinesModalClose,
    toggleGuidelinesModal,
  }
}
